export interface UserMoneyMetadata {
  market_id: string;
  currency: string;
  currency_symbol: string;
  currency_exponent: number;
}

export type UserRole = 'user' | 'admin';
export type UserStatus = 'active' | 'disabled';
export type AppleVerificationStatus = '' | 'verified' | 'revoked';
export type BooleanFilterValue = '' | 'true' | 'false';

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  user: '普通用户',
  admin: '管理员',
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  active: '正常',
  disabled: '已禁用',
};

export const APPLE_VERIFICATION_LABELS: Record<Exclude<AppleVerificationStatus, ''>, string> = {
  verified: '已验证',
  revoked: '已撤销',
};

export interface AdminUserRefDTO {
  id: number;
  email: string;
  nickname: string;
}

export interface UserSystemVersionDTO {
  platform: 'ios' | 'android';
  os_version: string;
  reported_at: string;
}
export interface UserSystemVersionCountDTO {
  platform: string;
  os_version: string;
  user_count: number;
}

export interface AdminUserListItemDTO extends UserMoneyMetadata {
  system_version?: UserSystemVersionDTO;
  id: number;
  email: string;
  nickname: string;
  avatar_url?: string;
  invite_code?: string;
  role: UserRole;
  status: UserStatus;
  admin_permissions?: string[];
  admin_full_access?: boolean;
  admin_parent_id?: number;
  created_at: string;
  updated_at: string;
  has_inviter: boolean;
  direct_inviter_id?: number;
  direct_inviter?: AdminUserRefDTO;
  wallet_balance_minor?: number;
  wallet_balance_fen: number;
  wallet_balance_yuan: string;
  wallet_frozen_minor?: number;
  wallet_frozen_fen: number;
  wallet_frozen_yuan: string;
  apple_account?: string;
  apple_verification_status?: AppleVerificationStatus;
  apple_verification_updated_at?: string;
  risk_tagged: boolean;
  risk_hit_count: number;
}

export interface AdminUserListResponseDTO {
  system_version_counts?: UserSystemVersionCountDTO[];
  total: number;
  page: number;
  page_size: number;
  items: AdminUserListItemDTO[];
}

export interface AdminWalletDTO extends UserMoneyMetadata {
  balance_minor?: number;
  balance_fen: number;
  balance_yuan: string;
  frozen_minor?: number;
  frozen_fen: number;
  frozen_yuan: string;
  total_earned_minor?: number;
  total_earned_fen: number;
  total_earned_yuan: string;
  total_withdrawn_minor?: number;
  total_withdrawn_fen: number;
  total_withdrawn_yuan: string;
  currency: string;
  updated_at?: string;
}

export interface AdminTeamStatsDTO {
  direct_invite_count: number;
  team_count: number;
  level1_count: number;
  level2_count: number;
  level3_count: number;
  level1_commission_minor?: number;
  level1_commission_fen: number;
  level1_commission_yuan: string;
  level2_commission_minor?: number;
  level2_commission_fen: number;
  level2_commission_yuan: string;
  level3_commission_minor?: number;
  level3_commission_fen: number;
  level3_commission_yuan: string;
  total_task_reward_minor?: number;
  total_task_reward_fen: number;
  total_task_reward_yuan: string;
  total_commission_minor?: number;
  total_commission_fen: number;
  total_commission_yuan: string;
  first_commission_count: number;
  updated_at?: string;
}

export interface AdminAppleVerificationDTO {
  apple_account: string;
  masked_account: string;
  masked_name: string;
  masked_directory_services_id: string;
  store_front: string;
  verification_status: Exclude<AppleVerificationStatus, ''>;
  verified_at?: string;
  revoked_at?: string;
  updated_at: string;
}

export interface AdminUserSummaryDTO {
  reward_count: number;
  settled_reward_amount_minor?: number;
  settled_reward_amount_fen: number;
  settled_reward_amount_yuan: string;
  commission_count: number;
  settled_commission_amount_minor?: number;
  settled_commission_amount_fen: number;
  settled_commission_amount_yuan: string;
  withdrawal_count: number;
  pending_withdrawal_count: number;
  assignment_count: number;
  pending_assignment_review_count: number;
  support_feedback_count: number;
}

export interface AdminFinanceComponentDTO {
  key: string;
  label: string;
  amount_minor?: number;
  amount_fen: number;
  amount_yuan: string;
  count: number;
  direction: 'snapshot' | 'income' | 'expense' | 'neutral';
  detail_kind: 'wallet' | 'wallet_transaction';
  wallet_transaction_type?: Exclude<AdminWalletTransactionType, ''>;
}

export interface AdminFinanceAnalysisDTO {
  balance_minor?: number;
  balance_fen: number;
  balance_yuan: string;
  frozen_minor?: number;
  frozen_fen: number;
  frozen_yuan: string;
  total_earned_minor?: number;
  total_earned_fen: number;
  total_earned_yuan: string;
  total_withdrawn_minor?: number;
  total_withdrawn_fen: number;
  total_withdrawn_yuan: string;
  transaction_net_minor?: number;
  transaction_net_fen: number;
  transaction_net_yuan: string;
  transaction_income_minor?: number;
  transaction_income_fen: number;
  transaction_income_yuan: string;
  transaction_expense_minor?: number;
  transaction_expense_fen: number;
  transaction_expense_yuan: string;
  balance_delta_minor?: number;
  balance_delta_fen: number;
  balance_delta_yuan: string;
  components: AdminFinanceComponentDTO[];
}

export interface RiskReasonStatDTO {
  code: string;
  count: number;
}

export interface AdminRiskSummaryDTO {
  risk_hit_count: number;
  unique_device_count: number;
  unique_apple_email_count: number;
  latest_device_guid?: string;
  latest_apple_email?: string;
  latest_bundle_id?: string;
  latest_app_version?: string;
  latest_risk_at?: string;
  latest_risk_reasons: string[];
  reason_breakdown: RiskReasonStatDTO[];
}

export interface AdminDeviceSummaryDTO {
  unique_device_count: number;
  unique_apple_email_count: number;
  latest_device_guid?: string;
  latest_apple_email?: string;
  latest_bundle_id?: string;
  latest_app_version?: string;
  latest_seen_at?: string;
}

export interface UserStatusChangeDTO {
  id: number;
  user_id: number;
  from_status: UserStatus;
  to_status: UserStatus;
  reason: string;
  operator_id: number;
  operator?: AdminUserRefDTO;
  created_at: string;
}

export interface AdminUserCoreDTO extends UserMoneyMetadata {
  id: number;
  email: string;
  nickname: string;
  avatar_url?: string;
  invite_code?: string;
  role: UserRole;
  status: UserStatus;
  admin_permissions?: string[];
  admin_full_access?: boolean;
  admin_parent_id?: number;
  created_at: string;
  updated_at: string;
  has_inviter: boolean;
}

export interface AdminUserDetailDTO {
  system_version?: UserSystemVersionDTO;
  user: AdminUserCoreDTO;
  direct_inviter?: AdminUserRefDTO;
  wallet?: AdminWalletDTO;
  team_stats?: AdminTeamStatsDTO;
  apple_verification?: AdminAppleVerificationDTO;
  summary: AdminUserSummaryDTO;
  finance_analysis?: AdminFinanceAnalysisDTO;
  risk: AdminRiskSummaryDTO;
  device: AdminDeviceSummaryDTO;
  recent_status_changes: UserStatusChangeDTO[];
}

export interface AdminUserStatusUpdateResponseDTO {
  user: {
    id: number;
    email: string;
    nickname: string;
    role: UserRole;
    status: UserStatus;
    admin_permissions?: string[];
    admin_full_access?: boolean;
    updated_at: string;
  };
  change?: UserStatusChangeDTO;
}

export interface AdminUserUpdateParams {
  email: string;
  nickname: string;
  password?: string;
  status: UserStatus;
}

export interface AdminUserUpdateResponseDTO {
  user: AdminUserCoreDTO;
}

export interface AdminUserReleaseAppleVerificationResponseDTO {
  released_apple_verification: AdminAppleVerificationDTO;
}

export type AdminWalletAdjustmentType = 'credit' | 'debit';

export interface AdminWalletAdjustmentDTO extends UserMoneyMetadata {
  id: number;
  user_id: number;
  operator_id: number;
  type: AdminWalletAdjustmentType;
  amount_minor?: number;
  amount_fen: number;
  amount_yuan: string;
  balance_before_minor?: number;
  balance_before_fen: number;
  balance_before_yuan: string;
  balance_after_minor?: number;
  balance_after_fen: number;
  balance_after_yuan: string;
  remark: string;
  wallet_transaction_id?: number;
  created_at: string;
}

export interface AdminWalletAdjustmentParams {
  type: AdminWalletAdjustmentType;
  amountMinor: number;
  remark: string;
}

export interface AdminWalletAdjustmentResponseDTO {
  adjustment: AdminWalletAdjustmentDTO;
  wallet: AdminWalletDTO;
}

export type AdminWalletTransactionType =
  | ''
  | 'task_reward'
  | 'commission_fixed'
  | 'commission_percentage'
  | 'withdrawal_freeze'
  | 'withdrawal_refund'
  | 'withdrawal_settle'
  | 'admin_credit'
  | 'admin_debit';

export interface AdminWalletTransactionDTO extends UserMoneyMetadata {
  id: number;
  type: Exclude<AdminWalletTransactionType, ''>;
  amount_minor?: number;
  amount_fen: number;
  amount_yuan: string;
  balance_after_minor?: number;
  balance_after_fen: number;
  balance_after_yuan: string;
  ref_order_id?: number;
  ref_order_type?: string;
  remark: string;
  created_at: string;
}

export interface AdminWalletTransactionListResponseDTO {
  total: number;
  page: number;
  page_size: number;
  items: AdminWalletTransactionDTO[];
}
